import { Scenario } from "@reco/shared/src/constants";
import type { Recommendation, RecommendationsData } from "@reco/shared/src/types/recommendation";
import { TargetType } from "./types/records";
import { isFlexibleScenario, parseFlexibleScenario } from "./utils/flexibleScenarios";
import { getCookieValue } from "./utils/storage";

export type RecommendationsRequestParams = {
  scenario: Scenario;
  recoHost: string;
  pageType: string;
  countryId: string;
  languageId: string;
  requestLimit: number;
  type: string;
  pageId?: string;
  contextId?: string;
  ssoId?: string;
  lidlId?: string;
  recoApiPost?: boolean;
  omnichannel?: string;
  onSale?: string;
  brandIds?: string;
  categoryIds?: string;
  campaignIds?: string;
  categoryViewedProducts?: string;
  brandViewedProducts?: string;
  categoryLevel?: string;
  zone?: string;
  antecedents?: string;
};

export default class RecommendationsAPIService {
  async get(params: RecommendationsRequestParams): Promise<RecommendationsData> {
    const baseUrl = this.buildBaseUrl(params);
    const queryParams = this.buildQueryParameters(params);
    const requestUrl = `${baseUrl}?${queryParams}`;

    try {
      const response = await this.fetchRecommendations(requestUrl, params);

      if (!response.ok) {
        throw new Error(`Request failed with ${response.status} status code!`);
      }

      const recoData: RecommendationsData = await response.json();
      return this.normalizeResponse(recoData, params);
    } catch (error) {
      console.error("[RecommendationsAPIService]: ", error);
      return { recommendationItems: [] };
    }
  }

  private buildBaseUrl(params: RecommendationsRequestParams): string {
    const { effectiveScenario, effectiveContextId } = this.deriveScenarioContext(params);

    const pathParts = [
      params.recoHost.replace(/\/$/, ""),
      "r/api/recommendations",
      params.countryId,
      params.languageId,
      "web",
      effectiveScenario,
      effectiveContextId,
    ].filter(Boolean);

    return pathParts.join("/");
  }

  private buildQueryParameters(params: RecommendationsRequestParams): URLSearchParams {
    const queryParams: Record<string, string> = {
      limit: String(params.requestLimit),
    };

    switch (params.scenario) {
      case Scenario.CUSTOMERS:
        this.addIfPresent<RecommendationsRequestParams>(queryParams, "ssoId", params.ssoId);
        this.addIfPresent<RecommendationsRequestParams>(queryParams, "lidlId", params.lidlId);
        break;

      case Scenario.FLEXIBLE_BESTSELLERS:
        this.addIfPresent<RecommendationsRequestParams>(
          queryParams,
          "omnichannel",
          params.omnichannel == "true" ? "true" : undefined,
        );
        this.addIfPresent<RecommendationsRequestParams>(
          queryParams,
          "onSale",
          params.onSale == "true" ? "true" : undefined,
        );
        this.addIfPresent<RecommendationsRequestParams>(queryParams, "brandIds", params.brandIds);
        this.addIfPresent<RecommendationsRequestParams>(queryParams, "categoryIds", params.categoryIds);
        this.addIfPresent<RecommendationsRequestParams>(queryParams, "campaignIds", params.campaignIds);
        break;

      case Scenario.PERSONAL_CATEGORY:
        this.addIfPresent<RecommendationsRequestParams>(queryParams, "categoryViewedProducts", params.contextId);
        break;

      case Scenario.PERSONAL_BRAND:
        this.addIfPresent<RecommendationsRequestParams>(queryParams, "brandViewedProducts", params.contextId);
        break;

      case Scenario.SHOPTHELOOK:
        this.addIfPresent<RecommendationsRequestParams>(queryParams, "pageType", params.pageType);
        this.addIfPresent<RecommendationsRequestParams>(queryParams, "pageId", params.pageId);
        break;

      case Scenario.MULTICROSS:
        this.addIfPresent<RecommendationsRequestParams>(queryParams, "antecedents", params.contextId);
        break;
    }

    if (isFlexibleScenario(params.type)) {
      const flexibleScenarioConfig = parseFlexibleScenario(params.type);

      if (flexibleScenarioConfig.targetType === TargetType.BRAND) {
        this.addIfPresent<RecommendationsRequestParams>(queryParams, "brandViewedProducts", params.contextId);
      } else if (flexibleScenarioConfig.targetType === TargetType.CATEGORY) {
        this.addIfPresent<RecommendationsRequestParams>(queryParams, "categoryViewedProducts", params.contextId);
        this.addIfPresent<RecommendationsRequestParams>(
          queryParams,
          "categoryLevel",
          String(flexibleScenarioConfig.categoryLevel),
        );
      }
    }

    if (params.countryId === "ES") {
      const zone = getCookieValue("zn");
      this.addIfPresent<RecommendationsRequestParams>(queryParams, "zone", zone);
    }

    return new URLSearchParams(queryParams);
  }

  private deriveScenarioContext(params: RecommendationsRequestParams) {
    let effectiveScenario = params.scenario;
    let effectiveContextId: string | undefined = undefined;

    if (isFlexibleScenario(params.type)) {
      effectiveScenario = parseFlexibleScenario(params.type).baseScenario;
      return { effectiveScenario, effectiveContextId };
    }

    switch (params.scenario) {
      case Scenario.FLEXIBLE_BESTSELLERS:
      case Scenario.PERSONAL_BRAND:
      case Scenario.PERSONAL_CATEGORY:
        effectiveScenario = Scenario.BESTSELLERS;
        break;

      case Scenario.CUSTOMERS:
      case Scenario.SHOPTHELOOK:
      case Scenario.MULTICROSS:
        break;
      default:
        effectiveContextId = encodeURIComponent(params.contextId || "allproducts");
        break;
    }

    return { effectiveScenario, effectiveContextId };
  }

  private normalizeResponse(data: RecommendationsData, params: RecommendationsRequestParams): RecommendationsData {
    let items = Array.isArray(data?.recommendationItems) ? data.recommendationItems : [];

    if (params.scenario === Scenario.BUNDLES && params.contextId) {
      const isLeadItemMissing = items[0]?.itemId !== params.contextId;

      if (isLeadItemMissing) {
        items = [{ itemId: params.contextId, relevance: 0, transactions: null } satisfies Recommendation, ...items];
      }
    }

    return {
      ...data,
      recommendationItems: items,
    };
  }

  private async fetchRecommendations(url: string, params: RecommendationsRequestParams) {
    const options: RequestInit = {
      mode: "cors",
      method: params.recoApiPost ? "POST" : "GET",
    };

    if (params.recoApiPost) {
      options.body = JSON.stringify({
        limit: params.requestLimit,
      });
      options.headers = { "Content-Type": "application/json" };
    }

    return fetch(url, options);
  }

  private addIfPresent<T>(
    target: Record<string, string>,
    key: keyof T & string,
    value: string | undefined | null,
  ): void {
    if (value) {
      target[key] = value;
    }
  }
}
